import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../style/ReuploadScan.css";
import "../../style/slotbooking.css";
import SlotTrio from "./components/SlotTrio";
import SlotTimeline from "./components/SlotTimeline";
import RescheduleSlotModal from "./components/RescheduleSlotModal";
import type { SlotScanPendingProps } from "./interface/ScheduledOrder ";
import {
  canUploadScanNow,
  formatCountdown,
  formatSlotDateLong,
  formatSlotTime,
  getSlotEndTime,
  getSlotStatus,
  msUntilScanOpens,
} from "../../utils/slotBooking";


const STATUS_BADGE: Record<string, string> = {
  Scheduled: "b-blue",
  "Ready for Scan": "b-amber",
  Missed: "b-red",
};

const SlotScanPending: React.FC<SlotScanPendingProps> = ({
  order,
  onUploadClick,
  onCancelClick,
  onRescheduled,
  onClose,
}) => {
  const navigate = useNavigate();
  const [, tick] = useState(0);
  const [showReschedule, setShowReschedule] = useState(false);

  // har 15 sec me refresh: slot start hote hi screen khud badal jaati hai
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, []);

  const status = getSlotStatus(order);
  const started = canUploadScanNow(order?.slotDate, order?.slotTime);
  const missed = status === "Missed";
  const remaining = msUntilScanOpens(order?.slotDate, order?.slotTime);

  return (
    <div className="slot-detail mt-4">
      {/* ---------- HEADER ---------- */}
      <div className="slot-detail-head">
        <div className="d-flex align-items-center gap-3">
          <button className="slotmod-view" onClick={onClose}>
            <i className="bi bi-arrow-left"></i> Back
          </button>
          <h4>Scheduled Order #{order?.orderID}</h4>
        </div>
        <span className={`slotmod-badge ${STATUS_BADGE[status] || "b-blue"}`}>
          ● {status.toUpperCase()}
        </span>
      </div>

      {/* ---------- SCHEDULE ---------- */}
      <div className="slot-detail-card">
        <h6>Schedule</h6>
        <SlotTrio slotDate={order?.slotDate} slotTime={order?.slotTime} />
        <div className="text-center">
          <span className="slot-badge-90">90 MINUTE ORDER</span>
          <div className="text-muted mt-2" style={{ fontSize: 13 }}>
            Delivery by {formatSlotTime(getSlotEndTime(order?.slotTime))}
          </div>
        </div>
      </div>

      {/* ---------- PATIENT ---------- */}
      <div className="slot-detail-card">
        <h6>Patient</h6>
        <div>{order?.patientName || "-"}</div>
      </div>

      {/* ---------- PROGRESS ---------- */}
      <SlotTimeline order={order} />

      {/* ---------- SCAN ---------- */}
      <div className={`slot-scan-box ${started && !missed ? "open" : "locked"}`}>
        <h6>Scan Upload</h6>

        {missed ? (
          <>
            <div className="slot-missed-note">
              The 90-minute window for this slot has passed. Please reschedule to continue.
            </div>
            <button className="btn blue full" disabled>
              Upload Scan
            </button>
          </>
        ) : !started ? (
          <>
            <div className="scan-icon"><i className="bi bi-lock-fill"></i></div>
            <h5>Scan upload isn't available yet</h5>
            <p>
              Your order is scheduled for
              <br />
              <strong>{formatSlotDateLong(order?.slotDate)}</strong>
              <br />
              <strong>{formatSlotTime(order?.slotTime)}</strong>
              <br />
              Scan upload will become available when your scheduled slot starts.
            </p>
            <div className="scan-timer">
              <i className="bi bi-stopwatch"></i> {formatCountdown(remaining)}
            </div>
            <div>
              <button className="btn blue full" disabled>
                Upload Scan
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="scan-icon ok"><i className="bi bi-check-circle-fill"></i></div>
            <h5>✓ SLOT STARTED</h5>
            <p>
              Your scheduled order is now active.
              <br />
              Scheduled: <strong>{formatSlotTime(order?.slotTime)}</strong> · Delivery Target:{" "}
              <strong>{formatSlotTime(getSlotEndTime(order?.slotTime))}</strong>
            </p>
            <button
              className="btn blue full scan-cta"
              onClick={() => onUploadClick?.(order?.caseID)}
            >
              <i className="bi bi-cloud-arrow-up"></i> + Upload Scan
            </button>
            <small className="scan-support">Supported files: .STL, .OBJ</small>
          </>
        )}
      </div>

      {/* ---------- ACTIONS ---------- */}
      <div className="slot-detail-actions mt-3">
        {(status === "Scheduled" || missed) && (
          <button className="btn btn-contact-section gray" onClick={() => setShowReschedule(true)}>
            Reschedule
          </button>
        )}
        <button className="btn btn-contact-section red" onClick={onCancelClick}>
          Cancel Order
        </button>
        <button
          className="btn btn-contact-section gray"
          onClick={() =>
            navigate("/dentist/ContactSupport", {
              state: { caseIds: [order?.caseID], orderId: order?.orderID },
            })
          }
        >
          Contact Support
        </button>
      </div>

      {showReschedule && (
        <RescheduleSlotModal
          show={showReschedule}
          order={order}
          onClose={() => setShowReschedule(false)}
          onRescheduled={() => {
            setShowReschedule(false);
            onRescheduled?.();
          }}
        />
      )}
    </div>
  );
};

export default SlotScanPending;
